import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { TemplateState } from '../../../../types/common/types';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { CONNECT_TO_DEPLOYER_OPTIONS, DeployerFormViewMetadata, DeployType } from '../types';
import { GzInputWebComponent } from '../../../../ui/gzInput/component/GzInputWebComponent';
import { DeployerGridWebComponent } from '../../deployerGrid';
import { WEB_COMPONENT_DEPLOYER_GRID } from '../../../../webcomponents.constants';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { CustomizableValidator } from '../../../../ui/gzForm/validators/customizable.validator';
import { PackageData } from '../../../types/package-data';
import { ValidationResult } from '../../../../ui/gzForm/formField/types/form-validator';
import { DeployerRelayData } from '../../deployerGrid/types';
import { ConnectToDeployerData } from '../../../types/connect-to-deployer-data';
import { translateFn } from '../../../../utils/translator.helper';
import { PackageActionContext } from '../../types/PackageActionContext';

export class PackageDeployerFormView extends BaseWebComponentView {
  public formGroup!: FormGroup;
  public updateLocation: string;
  public selectedItemsValidity: boolean;
  public selectedRelayData: DeployerRelayData;
  public companySuperAgents: DeployerRelayData[];
  public ecsHostname: string;
  private packageData: PackageData;
  private isInstallAgentDeployer = false;

  public dom: {
    $wrapper: HTMLElement;
    $customCommunicationSection: HTMLElement;

    $connectToFormField: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;
    $connectToFormFieldValidation: GzFormFieldWebComponent<GzDropdownWebComponent, string | number>;

    $selectedRelayFormField: HTMLDivElement;
    $selectedRelayFormFieldText: HTMLElement;
    $selectedRelayFormFieldValue: HTMLElement;

    $deployerGridForESR: DeployerGridWebComponent;

    $customCommunicationServerCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $customCommunicationServerField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  };

  getDomElements(): void {
    this.dom = {
      $wrapper: this.shadow.querySelector('#package-deployer-form-wrapper'),
      $customCommunicationSection: this.shadow.querySelector('#custom-communication-section'),

      $connectToFormField: this.shadow.querySelector('#deployer-form-field'),
      $connectToFormFieldValidation: this.shadow.querySelector('#deployer-form-field-validation'),

      $selectedRelayFormField: this.shadow.querySelector('#deployer-selected-relay'),
      $selectedRelayFormFieldText: this.shadow.querySelector('#selected-relay-field'),
      $selectedRelayFormFieldValue: this.shadow.querySelector('#selected-relay-field-value'),

      $deployerGridForESR: this.shadow.querySelector(WEB_COMPONENT_DEPLOYER_GRID),

      $customCommunicationServerCheckbox: this.shadow.querySelector('#custom-communication-checkbox'),
      $customCommunicationServerField: this.shadow.querySelector('#custom-communication-input'),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations(): void {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
  }

  setTranslatedStrings(): void {
    this.dom.$connectToFormField.label = translateFn('Connect to');
    this.dom.$selectedRelayFormFieldText.innerText = translateFn('Selected Relay:');
    this.dom.$customCommunicationServerCheckbox.Input.label = translateFn('Custom Communication Server IP/Hostname');

    this.dom.$customCommunicationServerField.label = translateFn('Server');
    this.dom.$connectToFormField.Input.noResultsFound = this.commonStrings.noResultsFound;
  }

  buildSuccessView(viewMetadata: DeployerFormViewMetadata): void {
    this.companySuperAgents = viewMetadata.companySuperAgents;
    this.packageData = viewMetadata.packageData;
    this.isInstallAgentDeployer = viewMetadata.isInstallAgentDeployer;
    this.setDefaultDataForCreate(viewMetadata);
    this.createFormGroup();
    this.dom.$deployerGridForESR.injectDependencies(this.dependencies);
    this.setConnectToDropdownDefaultOptions();

    if (viewMetadata.context === PackageActionContext.Edit) {
      this.formGroup.setDisabled(false);
    }

    if (this.isInstallAgentDeployer) {
      this.setConnectToOptionsByData();
    }

    if (this.packageData) {
      this.setConnectToOptionsByData();
      this.setFormValuesFromDatabase();
      return;
    }
    this.setDefaultFieldValues();
  }

  buildErrorView(error: Error): void {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  private createFormGroup(): void {
    this.formGroup = FormGroup.build({
      connectToValidation: this.dom.$connectToFormFieldValidation,
      settings: {
        deployType: this.dom.$connectToFormField,
        useCustomECS: this.dom.$customCommunicationServerCheckbox,
        ecsHostname: this.dom.$customCommunicationServerField,
      },
    });
    if (!this.dependencies.isCloud()) {
      toggleVisibility(this.dom.$customCommunicationSection, true);
    }
    this.formGroup.setDisabled(true);
  }

  private setDefaultDataForCreate(data: DeployerFormViewMetadata): void {
    this.updateLocation = data.updateLocationData.updateLocation;
    this.ecsHostname = data.updateLocationData.ecsHostname;
  }

  public setDefaultCustomCommunicationServer(): void {
    this.dom.$customCommunicationServerField.value = this.ecsHostname;
    this.dom.$customCommunicationServerField.disabled = !this.dom.$customCommunicationServerCheckbox.value;
  }

  public setConnectToDropdownDefaultOptions(): void {
    if (this.dependencies.isCloud()) {
      this.dom.$connectToFormField.Input.options = [
        {
          title: CONNECT_TO_DEPLOYER_OPTIONS[DeployType.BitdefenderCloud].title,
          value: CONNECT_TO_DEPLOYER_OPTIONS[DeployType.BitdefenderCloud].value,
        },
      ];
    } else {
      this.dom.$connectToFormField.Input.options = [
        {
          title: CONNECT_TO_DEPLOYER_OPTIONS[DeployType.GravityZoneAppliance].title,
          value: CONNECT_TO_DEPLOYER_OPTIONS[DeployType.GravityZoneAppliance].value,
        },
      ];
    }
  }

  public setDefaultFieldValues(): void {
    this.setDefaultConnectOption();
    this.setDefaultSelectedRelayField();
  }

  public setDefaultSelectedRelayField(): void {
    this.dom.$selectedRelayFormFieldValue.innerText = translateFn('N/A');
  }

  public setDefaultConnectOption(): void {
    if (this.dependencies.isCloud()) {
      if (this.isInstallAgentDeployer) {
        this.dom.$connectToFormField.value = CONNECT_TO_DEPLOYER_OPTIONS[DeployType.EndpointSecurityRelay].value;
        return;
      }
      this.dom.$connectToFormField.value = CONNECT_TO_DEPLOYER_OPTIONS[DeployType.BitdefenderCloud].value;
    } else {
      this.dom.$connectToFormField.value = CONNECT_TO_DEPLOYER_OPTIONS[DeployType.GravityZoneAppliance].value;
    }
  }

  public addCustomServerRequiredValidation(state: boolean): void {
    if (state) {
      this.dom.$customCommunicationServerField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
      this.dom.$customCommunicationServerField.setAttribute('required', 'required');
    } else {
      this.dom.$customCommunicationServerField.Validators = [];
      this.dom.$customCommunicationServerField.removeAttribute('required');
    }
  }

  public addConnectToValidators(connectionOption: number): void {
    if (connectionOption === CONNECT_TO_DEPLOYER_OPTIONS[DeployType.EndpointSecurityRelay].value) {
      this.dom.$connectToFormFieldValidation.Validators = [
        CustomizableValidator(this.customValidationGridSelection.bind(this)),
      ];
    } else {
      this.dom.$connectToFormFieldValidation.Validators = [];
      this.dom.$connectToFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
    }
  }

  private setFormValuesFromDatabase(): void {
    const companySuperAgent = this.companySuperAgents.filter(data => data._id === this.packageData.settings.supaId);

    if (companySuperAgent.length !== 0) {
      this.dom.$selectedRelayFormFieldValue.innerText = `${companySuperAgent[0].name} (${companySuperAgent[0].ip})`;
      this.selectedRelayData = {
        ip: companySuperAgent[0].ip,
        customHostname: companySuperAgent[0].customHostname,
        _id: companySuperAgent[0]._id,
      } as DeployerRelayData;
      this.selectedItemsValidity = true;
    }

    this.connectToFieldValueForEdit();
    this.formGroup.value = {
      settings: {
        useCustomECS: this.packageData.settings.useCustomECS,
        ecsHostname: this.packageData.settings.ecsHostname,
      },
    };
  }

  private connectToFieldValueForEdit(): void {
    let connectToField: ConnectToDeployerData;

    if (this.dependencies.isCloud()) {
      if (
        this.packageData?.settings.deployType === CONNECT_TO_DEPLOYER_OPTIONS[DeployType.EndpointSecurityRelay].value
      ) {
        connectToField = CONNECT_TO_DEPLOYER_OPTIONS[DeployType.EndpointSecurityRelay];
      } else {
        connectToField = CONNECT_TO_DEPLOYER_OPTIONS[DeployType.BitdefenderCloud];
      }
    } else {
      if (
        this.packageData?.settings.deployType === CONNECT_TO_DEPLOYER_OPTIONS[DeployType.EndpointSecurityRelay].value
      ) {
        connectToField = CONNECT_TO_DEPLOYER_OPTIONS[DeployType.EndpointSecurityRelay];
      } else {
        connectToField = CONNECT_TO_DEPLOYER_OPTIONS[DeployType.GravityZoneAppliance];
      }
    }

    if (this.dom.$connectToFormField.Input.options.length > 1) {
      this.formGroup.value = {
        settings: {
          deployType: connectToField.value,
        },
      };
    } else {
      this.setDefaultConnectOption();
      this.dom.$connectToFormField.disabled = true;
    }
    if (this.packageData.settings.modules?.updateServer) {
      this.dom.$connectToFormField.disabled = true;
    }
  }

  public setConnectToOptionsByData(): void {
    if (!(this.companySuperAgents && this.companySuperAgents.length > 0)) {
      this.setConnectToDropdownDefaultOptions();
      this.dom.$connectToFormField.disabled = true;
      return;
    }

    this.dom.$connectToFormField.disabled = false;
    if (!this.dependencies.isCloud()) {
      this.dom.$connectToFormField.Input.options = [
        {
          title: CONNECT_TO_DEPLOYER_OPTIONS[DeployType.GravityZoneAppliance].title,
          value: CONNECT_TO_DEPLOYER_OPTIONS[DeployType.GravityZoneAppliance].value,
        },
        {
          title: CONNECT_TO_DEPLOYER_OPTIONS[DeployType.EndpointSecurityRelay].title,
          value: CONNECT_TO_DEPLOYER_OPTIONS[DeployType.EndpointSecurityRelay].value,
        },
      ];
      return;
    }

    if (this.isInstallAgentDeployer) {
      this.dom.$connectToFormField.Input.options = [
        {
          title: CONNECT_TO_DEPLOYER_OPTIONS[DeployType.EndpointSecurityRelay].title,
          value: CONNECT_TO_DEPLOYER_OPTIONS[DeployType.EndpointSecurityRelay].value,
        },
      ];
      this.dom.$connectToFormField.disabled = true;
      return;
    }

    this.dom.$connectToFormField.Input.options = [
      {
        title: CONNECT_TO_DEPLOYER_OPTIONS[DeployType.BitdefenderCloud].title,
        value: CONNECT_TO_DEPLOYER_OPTIONS[DeployType.BitdefenderCloud].value,
      },
      {
        title: CONNECT_TO_DEPLOYER_OPTIONS[DeployType.EndpointSecurityRelay].title,
        value: CONNECT_TO_DEPLOYER_OPTIONS[DeployType.EndpointSecurityRelay].value,
      },
    ];
  }

  private customValidationGridSelection(): ValidationResult {
    if (!this.selectedItemsValidity) {
      this.dom.$deployerGridForESR.setWarningVisibility(true);
      return {
        valid: false,
        error: '',
      };
    }
    this.dom.$deployerGridForESR.setWarningVisibility(false);
    return {
      valid: true,
    };
  }
}
